import { RootState } from '@Common/models/redux/store';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { CitationData, CitationSourceEnum, IPublicationInfo } from 'src/citation-generator/types';
import { topApaStyleIds, topMlaStyleIds } from 'src/citation-generator/utils';
import { setErrorAndWarning } from './actionCreators';
import { useEditFormDispatch, useEditFormState } from './context';
import {
  FieldConfig,
  FieldConfigObject,
  FormValueKeys,
  SetErrorAndWarningPayload,
  Validator,
} from './types';
import { validators } from './utils';

/** This variable contains overridden configuration for specific citation styles */
const fieldsConfigPerStyle = Object.fromEntries([
  ...topApaStyleIds.concat(topMlaStyleIds).map((id) => [
    /** Citation style id */
    id,
    /** FieldConfig object specific to `topApaStyleIds` and `topMlaStyleIds` */
    {
      Book: {
        city: {
          /**
           * by default we are using warning validators for all styles
           * but, they are not needed for APA and MLA styles
           */
          validators: [],
        },
        state: {
          /**
           * by default we are using warning validators for all styles
           * but, they are not needed for APA and MLA styles
           */
          validators: [],
        },
      },
    },
  ]),
] as Array<[string, Partial<FieldConfig>]>);

const fieldsConfig: FieldConfig = {
  [CitationSourceEnum.WEBSITE]: {
    title: {
      validators: [validators.error.empty],
    },
    contributors: {
      // validators: [validators.warn.emptyContributors],
    },
    containerTitle: {
      label: 'Website Title',
      validators: [validators.warn.empty],
    },
    url: {
      validators: [validators.warn.empty],
    },
    publishDate: {
      validators: [validators.warn.emptyDate],
    },
    accessDate: {},
    isbn: {},
  },
  [CitationSourceEnum.BOOK]: {
    title: {
      validators: [validators.error.empty],
    },
    contributors: {
      // validators: [validators.warn.emptyContributors],
    },
    containerTitle: {},
    publishDate: {
      validators: [validators.warn.emptyDate],
    },
    publisher: {
      validators: [validators.warn.empty],
    },
    city: {
      validators: [validators.warn.empty],
    },
    state: {
      validators: [validators.warn.empty],
    },
    medium: {},
    volume: {},
    totalVolumes: {},
    edition: {},
    collectionTitle: {},
    citedPages: {},
    url: {},
    doi: {},
    isbn: {},
  },
  [CitationSourceEnum.JOURNAL]: {
    title: {
      validators: [validators.error.empty],
    },
    contributors: {
      // validators: [validators.warn.emptyContributors],
    },
    containerTitle: {
      label: 'Journal Title',
      validators: [validators.warn.empty],
    },
    url: {},
    publishDate: {
      validators: [validators.warn.emptyDate],
    },
    status: {
      options: [
        { label: 'Published', value: 'published', default: true },
        { label: 'In Press', value: 'in-press' },
      ],
    },
    doi: {
      validators: [validators.warn.empty],
    },
    volume: {},
    issue: {},
    collectionTitle: {},
    citedPages: {},
    isbn: {},
    source: {
      label: 'Library Database',
    },
  },
};

const defaultLabels: Partial<Record<FormValueKeys, string>> = {
  title: 'Title',
  containerTitle: 'Container Title',
  publishDate: 'Date Published',
  accessDate: 'Date Accessed',
  publisher: 'Publisher/Sponsor',
  city: 'City',
  state: 'State',
  url: 'URL',
  doi: 'DOI',
  citedPages: 'Page(s)',
  volume: 'Volume',
  issue: 'Issue',
  totalVolumes: 'Total Volumes',
  edition: 'Edition',
  collectionTitle: 'Series',
  status: 'Publication Status',
  medium: 'Medium',
  source: 'Source',
};

export const useFieldConfig = (valueKey: FormValueKeys): FieldConfigObject<typeof valueKey> => {
  const ref = useRef<FieldConfigObject<typeof valueKey>>({});
  const citationStyle = useSelector((state: RootState) => state.ctgr.citationStyle);
  const sourceType = useEditFormState((state) => state.sourceType);

  ref.current.label = fieldsConfig[sourceType][valueKey]?.label || defaultLabels[valueKey] || '';
  ref.current.options = fieldsConfig[sourceType][valueKey]?.options;

  if (
    Object.prototype.hasOwnProperty.call(
      fieldsConfigPerStyle[citationStyle]?.[sourceType]?.[valueKey] || {},
      'show'
    )
  ) {
    ref.current.show = fieldsConfigPerStyle[citationStyle]?.[sourceType]?.[valueKey]?.show;
  } else {
    ref.current.show = Object.prototype.hasOwnProperty.call(fieldsConfig[sourceType], valueKey);
  }

  return ref.current;
};

export const useMultipleFieldConfig = (
  valueKeys: FormValueKeys[]
): Array<FieldConfigObject<typeof valueKeys[number]> & { valueKey: typeof valueKeys[number] }> => {
  const sourceType = useEditFormState((state) => state.sourceType);

  return valueKeys
    .map((valueKey) => ({
      valueKey,
      label: fieldsConfig[sourceType][valueKey]?.label || defaultLabels[valueKey] || '',
      show: Object.prototype.hasOwnProperty.call(fieldsConfig[sourceType], valueKey),
    }))
    .filter(({ show }) => show);
};

export const getApplicableFields = (sourceType: CitationSourceEnum, citationStyleId?: string) => {
  const applicableFields = <FormValueKeys[]>Object.keys(fieldsConfig[sourceType]);

  if (citationStyleId && fieldsConfigPerStyle[citationStyleId]?.[sourceType]) {
    return applicableFields.filter((field) =>
      Object.prototype.hasOwnProperty.call(
        fieldsConfigPerStyle[citationStyleId][sourceType]?.[field] || {},
        'show'
      )
        ? fieldsConfigPerStyle[citationStyleId][sourceType]?.[field]?.show
        : true
    );
  }

  return applicableFields;
};

export const isFieldApplicableForSourceType = (
  sourceType: CitationSourceEnum,
  field: keyof IPublicationInfo
) => Object.prototype.hasOwnProperty.call(fieldsConfig[sourceType], field);

export const useFieldValidator = (valueKey: FormValueKeys) => {
  const { resetter, ...formState } = useEditFormState((state) => state);
  const sourceType = formState.sourceType;
  const isEdit = useEditFormState((state) => state.isEdit);
  const value = useEditFormState((state) => state[valueKey]);
  const warning = useEditFormState((state) => state.warnings[valueKey]);
  const error = useEditFormState((state) => state.errors[valueKey]);
  const editFormDispatch = useEditFormDispatch();
  const citationStyle = useSelector((state: RootState) => state.ctgr.citationStyle);

  useEffect(() => {
    const currentValidators =
      fieldsConfigPerStyle[citationStyle]?.[formState.sourceType]?.[valueKey]?.validators ||
      fieldsConfig[formState.sourceType][valueKey]?.validators;

    if (!currentValidators) {
      return;
    }

    const errorsAndWarnings: SetErrorAndWarningPayload = {
      key: valueKey,
    };

    currentValidators.forEach((validatorFn: Validator<any>) => {
      const out = validatorFn(value, valueKey, formState);
      // TODO: handle array output of warning/errors
      if (!error && out && 'warning' in out) {
        errorsAndWarnings.warning = out.warning;
      } else if (out && 'error' in out) {
        errorsAndWarnings.error = out.error;
      } else {
        //
      }
    });

    if (
      // if new error message is there, set error message
      // do not update if old error is already there
      (!error && errorsAndWarnings.error) ||
      // if new warning is there, set warning
      // do not update if old warning is already there
      (!error && !warning && errorsAndWarnings.warning) ||
      // if existing error/warning was there
      // and, no new error/warning was computed, it will be removed
      ((error || warning) && !errorsAndWarnings.error && !errorsAndWarnings.warning)
    ) {
      editFormDispatch(setErrorAndWarning(errorsAndWarnings));
    }
  }, [value, resetter, citationStyle, sourceType]);

  useEffect(() => {
    if (!isEdit) {
      // remove errors/warning on mount when composing new citation
      editFormDispatch(setErrorAndWarning({ key: valueKey }));
    }
  }, [isEdit, resetter]);

  return { error, warning };
};

export const hasMissingInfo = (cardData: CitationData, citationStyle: string) => {
  // should be run with useMemo
  const sourceType = cardData.citationSource;

  const has = getApplicableFields(sourceType, citationStyle).some((field) => {
    if (field === 'contributors') {
      return cardData.contributors?.some((c) => !c.lastName || !c.firstName);
    }

    const currentValidators =
      fieldsConfigPerStyle[citationStyle]?.[sourceType]?.[field]?.validators ||
      fieldsConfig[sourceType][field]?.validators;

    return currentValidators?.length
      ? currentValidators.some((validatorFn: Validator<any>) => {
          const getterKey = field === 'sourceType' ? 'citationSource' : field;
          const value =
            getterKey in cardData.publicationInfo
              ? cardData.publicationInfo[getterKey as keyof IPublicationInfo]
              : cardData[getterKey as 'citationSource'];
          const out = validatorFn(value, field);

          return (
            out &&
            ('warning' in out || (Array.isArray(out) && out.some((o) => o && 'warning' in o)))
          );
        })
      : false;
  });

  return has;
};
